import axios, { AxiosInstance } from 'axios';
import { getAccessToken, getUserId, removeAccessToken } from '../utils/authUtils';
import { isDevAuthBypassEnabled } from '../config/authDomain';
import { useAuthStore } from '../store/authStore';

// SSGP v3 base URL — falls back to the local dev port (5116) used by the
// agents-simple service per docs/todo.md §"Quick resume cheat-sheet".
export const API_BASE_URL =
  import.meta.env.VITE_SSGP_V3_API_BASE_URL || 'http://localhost:5116';

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 10 minute default timeout for long-running workflows
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth tokens
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token from secure storage
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // SMA-8: the spoofable `x-user-id` header is now only honored by the backend
    // in dev (Auth:AllowUnsignedTokens). Real sessions are scoped from the
    // validated JWT `sub`, so only send the header under the dev-auth bypass —
    // it keeps local/.http flows working and is ignored everywhere else.
    if (isDevAuthBypassEnabled()) {
      const userId = getUserId();
      if (userId) {
        config.headers['x-user-id'] = userId;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // A 401 only means "your session is bad" if you HAD one. The app is now
      // browsable signed out, so user-scoped endpoints (assets, provider keys)
      // answer 401 as a matter of course for visitors — clearing state on those
      // is meaningless noise, and reacting to every 401 risks tearing down a
      // real session because of one unrelated request.
      if (getAccessToken()) {
        removeAccessToken();
        useAuthStore.getState().logout();
        console.warn('Session expired. Please log in again.');
      }
      // Anonymous 401s fall through untouched: the caller decides what to show,
      // which for a signed-out visitor is a sign-in prompt, not an error.
    }
    return Promise.reject(error);
  }
);

/**
 * Bearer header for requests made outside axios — the fetch-based SSE services.
 * Axios requests get the token from the interceptor above, per request;
 * `apiClient.defaults.headers` is never populated, so reading it there sent
 * those requests unauthenticated.
 */
export function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
